import { useRef, useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { discoArrayToProductImageStruct, getProductImageAltText } from "helpers/product-image-helpers";
import { PdpImageZoomModalName, analytics, flagsAndExperimentNames, UPDP_BUYBOX_MODES } from "rtr-constants";
import Header from "./header";
import PDPBreadCrumbs from "../products/pdp-breadcrumbs";
import PDPImagery from "../pdp/pdp-imagery";
import PdpImageZoomModal from "../products/pdp-image-zoom-modal.jsx";
import ReviewModal from "./reviews/review-modal";
import ProductDetails from "../products/product-details";
import ProductImageRotation from "helpers/product-image-rotation";
import ProductInfo from "../products/product-info/product-info";
import ReviewThumbNailCarousel from "./reviews/review-thumbnail/review-thumbnail-carousel";
import AnchorProductAggregateRating from "components/source/products/anchor-product-aggregate-rating";
import sharedActions from "actions/shared-actions";
import useSailthruConfig from "./helpers/use-sailthru-config";
import PDPInlineReviews from "../reviews/pdp-inline-reviews";
import { MODAL_NAMES } from "constants/modal-names";
import { setPDPBuyBoxMode } from "slices/pdp-slice";
import PlansDrawerModal from "../acquisition/plans_drawer/plans-drawer-modal";
import MembershipBuyBox from "./buy-boxes/membership-buy-box";
import ClearanceBuyBox from "./buy-boxes/clearance-buy-box";
import ReserveBuyBox from "./buy-boxes/reserve-buy-box";
import BuyNowBuyBox from "./buy-boxes/buynow-buy-box";
import maternityHelper from "helpers/maternity-helper";
import PdpCarousels from "../pdp/pdp-carousels";
import { Flags } from "../hoc/with-feature-flags";
import smartComponentActions from "actions/smart-component-actions";
import MembershipUpgradesHelpers from "helpers/membership-upgrades-helpers";
import { isAdmin } from "components/source/hoc/with-user-data";
import PDPAdminDetail from "components/source/products/pdp-admin-detail.tsx";
import ComingSoonPDPContent from "components/source/products/coming-soon-pdp-content";
import PromoExclusion from "components/source/products/promo-exclusion";
import ProductsActions from "actions/products-actions";
import { useLoadThumbnailPhotos } from "./reviews/helpers/review-images-helper";
import { shouldExcludePromo } from "helpers/product-drawer-helper";
import { getPDPBuyboxMode } from "./buy-boxes/helpers/buybox-helper";
import { pdp_page_view, pdp_size_selection } from "./helpers/pixel-fires";
import UpsellBonusStyle from "../reservations/upsell-bonus-style/upsell-bonus-style";
import ModeSwitcher from "./mode-switcher";
import useFetchPricing from "./helpers/use-fetch-pricing";
import productRatingsActions from "actions/product-ratings-actions";
import ItemUnavailable from "components/source/reservations/item-unavailable";
import HeapHelpers from "helpers/heap-helpers";
import SizeGuideModal from "../products/product-info/size-guide-modal";

export default function PDP() {
  useSailthruConfig();
  useFetchPricing();
  const [featureImageSelected, setFeatureImageSelected] = useState(null);
  // Note: State used to render the create short list button for a 'Coming Soon' item
  const [showComingSoonShortList, setShowComingSoonShortList] = useState(false);
  const inlineReviewsRef = useRef(null);

  const userData = useSelector(state => state.userData);

  const staticProduct = useSelector(state => state.staticProduct);
  const isMobileViewport = useSelector(state => state.browser?.isMobileViewport);
  const workingFilters = useSelector(state => state.workingFilters) || {};
  const momentsWithCount = useSelector(state => state.reviewsThumbnailPhotos);
  const gridProductDrawer = useSelector(state => state.gridProductDrawer);
  const membershipState = useSelector(state => state.membershipState);
  const ceBagCount = useSelector(state => state.ceBagCount);
  const buyBoxMode = useSelector(state => state.pdp?.pdpBuyBoxMode);
  const selectedSizeSku = useSelector(state => state.selectedSizeSku);

  const reviewStarRating = useSelector(state => state.reviews?.averageRating ?? 0);
  const reviewModalOpen = useSelector(state => state.pdp?.reviewModal?.isOpen);
  const numOfReviews = useSelector(state => state.reviews?.count ?? 0);
  const unlimitedAddOnFlag = useSelector(state => state.featureFlags?.[Flags.UNLIMITED_ALWAYS_ON_ADD_ONS]);
  const promoExclusionFlag = useSelector(
    state => state.flagsAndExperiments?.[flagsAndExperimentNames.CATALOG_SERVICES_SHOW_PROMO_EXCLUSION]
  );
  const enableExclusiveBadgeFlag = useSelector(
    state => state.flagsAndExperiments?.[flagsAndExperimentNames.CATALOG_SERVICES_EXCLUSIVE_BADGE_ON_PDP]
  );
  const reviewsThumbnailPhotos = momentsWithCount?.moments;
  const dispatch = useDispatch();

  useLoadThumbnailPhotos(staticProduct.productId);

  // TODO: Migrate lens logic to date filter logic
  useEffect(() => {
    pdp_page_view(dispatch);
    dispatch(ProductsActions.setMembershipLensOnPageLoad(userData));
    dispatch(smartComponentActions.getPDPContent());
  }, []);

  useEffect(() => {
    let pdpUrl;
    if (typeof window === "undefined") {
      pdpUrl = "";
    } else {
      pdpUrl = window.location?.search;
    }

    dispatch(setPDPBuyBoxMode(getPDPBuyboxMode(pdpUrl, staticProduct.merchandisingCategory, workingFilters?.date)));
  }, [workingFilters?.date, staticProduct.merchandisingCategory]);

  useEffect(() => {
    pdp_size_selection(selectedSizeSku, staticProduct.productId, gridProductDrawer?.pdpUrl);
  }, [selectedSizeSku, staticProduct.productId, gridProductDrawer?.pdpUrl]);

  useEffect(() => {
    if (staticProduct?.isComingSoon) {
      dispatch(productRatingsActions.loadUserProductRatings());
    }
  }, []);

  useEffect(() => {
    HeapHelpers.fireHeapEvent("universalPDP", {});
  }, []);

  const showMaternityBumpImage = maternityHelper.showMaternityBumpImage(userData);
  const isBuyNow = buyBoxMode === "buy";

  // Size guide modal
  const sizeGuideModalOpen = useSelector(state => state?.pdp?.sizeGuideModalOpen);

  // FIXME: setting maternity flag to false for now
  const imageRotation = ProductImageRotation(
    workingFilters?.canonicalSizes || [],
    staticProduct,
    showMaternityBumpImage
  );
  const altText = getProductImageAltText(staticProduct.displayName, staticProduct.designerDisplayName);
  const images = discoArrayToProductImageStruct(staticProduct.images);
  const displayedModal = useSelector(state => state.displayedModal);
  const isAccessory = Array.isArray(staticProduct?.skus) && staticProduct.skus.length === 1;

  const annotationSuffix = MembershipUpgradesHelpers.getUpgradeAnnotationAppendCE(
    membershipState,
    userData,
    ceBagCount,
    unlimitedAddOnFlag
  );

  const promoExclusionComponent = () => {
    const isBuyMode = buyBoxMode === UPDP_BUYBOX_MODES.BUY;
    const isClearanceMode = buyBoxMode === UPDP_BUYBOX_MODES.CLEARANCE;

    if (!isBuyMode && !isClearanceMode) {
      return null;
    }

    const excludePromo = shouldExcludePromo({
      product: staticProduct,
      promoExclusionFlag,
      ...(isClearanceMode && { expectedMerchandisingCategory: "Clearance" }),
    });

    return <PromoExclusion shouldExclude={excludePromo} />;
  };

  const isExclusiveCapsuleBadgeDisplayed = enableExclusiveBadgeFlag && staticProduct?.exclusiveCapsule;

  return (
    <div className="pdp-content">
      <div data-test-id="universal_pdp" className="pdp-main-section media-query-layout">
        <PDPImagery
          altText={altText}
          className={"product-images"}
          isCloudZoomEnabled={false}
          images={images}
          imageRotation={imageRotation}
          isMobileViewport={isMobileViewport}
          onClick={(index, ref) => {
            setFeatureImageSelected({ index, ref });
            dispatch(sharedActions.displayModal(PdpImageZoomModalName));
          }}
        />
        <div className="pdp-dynamic-section flex flex-col gap-6">
          <div>
            <Header showComingSoonShortList={showComingSoonShortList} />
            {promoExclusionComponent()}
            {isExclusiveCapsuleBadgeDisplayed && (
              <div className="flex mt-2" data-test-id="exclusive-badge">
                <span className="flex mt-1 py-1 px-2 border rounded-full text-center text-sm font-medium">
                  Exclusive
                </span>
              </div>
            )}
          </div>

          {staticProduct?.isComingSoon && (
            <ComingSoonPDPContent
              location={analytics.ACTION_LOCATIONS.PDP}
              product={staticProduct}
              setShowComingSoonShortList={setShowComingSoonShortList}
            />
          )}
          {numOfReviews ? (
            <div className="flex flex-col gap-4" data-test-id="review-stars-count">
              <AnchorProductAggregateRating
                productId={staticProduct.productId}
                reviews={{
                  count: numOfReviews,
                  averageRating: reviewStarRating,
                }}
                redirectUrl={gridProductDrawer?.pdpUrl}
              />
              {!isMobileViewport && <ReviewThumbNailCarousel productId={staticProduct.productId} />}
            </div>
          ) : null}
          {!staticProduct?.isComingSoon && staticProduct?.stage !== "UNPUBLISHED" && (
            <div className="mb-4">
              {buyBoxMode === "membership" && <MembershipBuyBox />}
              {buyBoxMode === "buy" && <BuyNowBuyBox />}
              {buyBoxMode === "clearance" && <ClearanceBuyBox />}
              {buyBoxMode === "reserve" && <ReserveBuyBox />}
            </div>
          )}
          {!staticProduct?.isComingSoon && staticProduct?.stage !== "UNPUBLISHED" && <ModeSwitcher />}
          {staticProduct.stage === "UNPUBLISHED" && (
            <div className="mb-4">
              <ItemUnavailable id={staticProduct.productId} useNewGrid={true} remaining={0} unexpectedError={null} />
            </div>
          )}
        </div>

        <PdpImageZoomModal
          altText={altText}
          imageRotation={imageRotation}
          images={images}
          isMobileViewport={isMobileViewport}
          openingElement={featureImageSelected}
        />
      </div>

      <ProductInfo
        selected={null}
        showOptionalDetails={isBuyNow}
        isMobileViewport={isMobileViewport}
        imageRotation={imageRotation}
        images={images}
        displayName={staticProduct.displayName}
        designerDisplayName={staticProduct.designerDisplayName}
        productId={staticProduct.productId}
        fitNotes={staticProduct.fitNotes}
        stylistNotes={staticProduct.stylistNotes}
        productDetails={staticProduct.productDetails}
      />

      <SizeGuideModal
        designerName={staticProduct?.designerDisplayName}
        designerNotes={staticProduct?.fitNotes}
        isOpen={sizeGuideModalOpen}
      />

      {!isMobileViewport && (
        <ProductDetails
          selected={null}
          showOptionalDetails={isBuyNow}
          isMobileViewport={isMobileViewport}
          imageRotation={imageRotation}
          images={images}
          displayName={staticProduct.displayName}
          designerDisplayName={staticProduct.designerDisplayName}
          productId={staticProduct.productId}
          fitNotes={staticProduct.fitNotes}
          stylistNotes={staticProduct.stylistNotes}
          productDetails={staticProduct.productDetails}
        />
      )}

      <PdpCarousels
        annotationSuffix={annotationSuffix}
        dispatch={dispatch}
        isRentable={staticProduct.merchandisingCategory === "Rental"}
        isAccessory={isAccessory}
        isUnavailableToRent={false}
        isUPDP={true}
      />
      {!isMobileViewport && <div data-test-id="desktop-reviews-partial-anchor" id="reviews-partial" />}
      {!isMobileViewport && (
        <PDPInlineReviews
          dispatch={dispatch}
          key="0"
          ref={inlineReviewsRef}
          product={staticProduct}
          shouldLoadReviews={true}
          writingReview={false} // @TODO: Ensure writingReview works as in PDP - did some digging but couldn't find where
          // this value is set in legacy PDP (seems to be present in ruby)
        />
      )}

      <PDPBreadCrumbs />
      {reviewsThumbnailPhotos?.length > 0 && reviewModalOpen && (
        <ReviewModal productDisplayName={staticProduct?.displayName} productId={staticProduct?.productId} />
      )}
      {isAdmin(userData) && (
        <PDPAdminDetail id={staticProduct?.productId} published={staticProduct?.stage === "PUBLISHED"} />
      )}
      {!gridProductDrawer && buyBoxMode === "reserve" && <UpsellBonusStyle />}
      <PlansDrawerModal isOpen={displayedModal === MODAL_NAMES.PLANS_DRAWER} />
    </div>
  );
}
